import { useDecide } from '@optimizely/react-sdk';

export type FeatureFlagResultT = {
  enabled: boolean;
  isLoading: boolean;
  variables: Record<string, unknown>;
  variationKey: string | null;
};

const flagOffResult: FeatureFlagResultT = {
  enabled: false,
  isLoading: false,
  variables: {},
  variationKey: null,
};

/**
 * Reads a single Optimizely feature flag by key.
 *
 * Reports the flag as off whenever a decision is unavailable: while loading,
 * on SDK error, and when no <OptimizelyWrapper> provider is present.
 *
 * The try/catch exists because `useDecide` throws when it cannot find an
 * <OptimizelyProvider> ancestor, and the SDK does not export its context for us
 * to check directly. This is safe: the throw happens after exactly one
 * `useContext` call, deterministically, and provider presence is fixed at the
 * app root, so hook order is stable for any mounted component.
 *
 * `isLoading` is surfaced rather than swallowed so callers can avoid rendering
 * the wrong UI and then swapping it out.
 */
export const useFeatureFlag = (flagKey: string): FeatureFlagResultT => {
  let decideResult: ReturnType<typeof useDecide>;

  try {
    // Safe despite the rule: see the doc block above. The throw is deterministic
    // and happens after exactly one useContext call, so hook order stays stable.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    decideResult = useDecide(flagKey);
  } catch {
    return flagOffResult;
  }

  const {
    decision,
    error,
    isLoading,
  } = decideResult;

  if (isLoading || error || !decision) {
    return { ...flagOffResult, isLoading };
  }

  return {
    enabled: decision.enabled,
    isLoading: false,
    variables: decision.variables,
    variationKey: decision.variationKey,
  };
};
